import React, { useState } from 'react';
import { Alert, Form, Row, Col, Button, Table } from 'react-bootstrap';
import { ILicense } from '../index';
import styles from './LicensesTab.module.css';
import { FiPlus, FiTrash2 } from '../../../utils/icons';
import { useT } from '../../../i18n';

interface LicensesTabProps {
  licenses: ILicense[];
  setLicenses: React.Dispatch<React.SetStateAction<ILicense[]>>;
  readOnly?: boolean;
}

const initialLicenseState = {
  id: 0,
  tipo: 'Módulos' as const,
  status: 'Ativa' as const,
};

const LicensesTab: React.FC<LicensesTabProps> = ({ licenses, setLicenses, readOnly = false }) => {
  const t = useT();
  const [newLicense, setNewLicense] = useState<Omit<ILicense, 'id'>>(initialLicenseState);

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewLicense(prev => ({ ...prev, [name]: value as any }));
  };

  const handleAddLicense = () => {
    setLicenses(prev => [...prev, { ...newLicense, id: Date.now() }]);
  };

  const handleRemoveLicense = (id: number) => {
    setLicenses(prev => prev.filter(license => license.id !== id));
  };

  return (
    <div>
      {readOnly ? (
        <Alert variant="info" className="mb-3">
          {t('companyRegistration.licenses.readOnlyAlert')}
        </Alert>
      ) : (
        <Form as={Row} className={`g-3 ${styles.formRow}`}>
          <Col md={5}>
            <Form.Group controlId="licenseType">
              <Form.Label>{t('companyRegistration.licenses.tipo')}</Form.Label>
              <Form.Select name="tipo" value={newLicense.tipo} onChange={handleInputChange}>
                <option value="Módulos">{t('companyRegistration.licenses.modulos')}</option>
                <option value="Serviços">{t('companyRegistration.licenses.servicos')}</option>
                <option value="Mentoria">{t('companyRegistration.licenses.mentoria')}</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group controlId="licenseStatus">
              <Form.Label>{t('companyRegistration.licenses.status')}</Form.Label>
              <Form.Select name="status" value={newLicense.status} onChange={handleInputChange}>
                <option value="Ativa">{t('companyRegistration.licenses.ativa')}</option>
                <option value="Inativa">{t('companyRegistration.licenses.inativa')}</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2} className={styles.buttonContainer}>
            <Button variant="primary" onClick={handleAddLicense}><FiPlus /> {t('companyRegistration.licenses.add')}</Button>
          </Col>
        </Form>
      )}

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>{t('companyRegistration.licenses.tipo')}</th>
            <th>{t('companyRegistration.licenses.status')}</th>
            {!readOnly && <th>{t('companyRegistration.licenses.tableActions')}</th>}
          </tr>
        </thead>
        <tbody>
          {licenses.length === 0 ? (
            <tr>
              <td colSpan={readOnly ? 2 : 3} className="text-center text-muted py-3">
                {t('companyRegistration.licenses.empty')}
              </td>
            </tr>
          ) : (
            licenses.map(license => (
              <tr key={license.id}>
                <td>{license.tipo === 'Módulos' ? t('companyRegistration.licenses.modulos') : license.tipo === 'Serviços' ? t('companyRegistration.licenses.servicos') : t('companyRegistration.licenses.mentoria')}</td>
                <td>{license.status === 'Ativa' ? t('companyRegistration.licenses.ativa') : t('companyRegistration.licenses.inativa')}</td>
                {!readOnly && (
                  <td>
                    <Button variant="outline-danger" size="sm" onClick={() => handleRemoveLicense(license.id)}><FiTrash2 /> {t('companyRegistration.licenses.remove')}</Button>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default LicensesTab;